import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { concat } from '@ember/helper';
import { debounce } from '@ember/runloop';
import { PhMagnifyingGlass, PhX } from 'ember-phosphor-icons';
import { and, not } from 'tio-ui/utilities';
import FormField from './form-field';

const DEFAULT_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// SearchInput – reimagined search-specific input with icon + clear button
// ---------------------------------------------------------------------------

export type SearchInputSize = 'sm' | 'md' | 'lg';

interface SearchInputSignature {
  Args: {
    /** Current search value */
    value?: string;
    /** Called on every keystroke with the new value (debounced by default) */
    onChange?: (value: string) => void;
    /** Called when the user presses Enter */
    onSearch?: (value: string) => void;
    /** Called when the clear button is clicked */
    onClear?: () => void;

    /** Label displayed above the input */
    label?: string;
    /** Hint text */
    hint?: string;
    /** Placeholder text @default "Search…" */
    placeholder?: string;
    /** Size variant @default "md" */
    size?: SearchInputSize;
    /** Disable the input */
    isDisabled?: boolean;
    /** Debounce delay in ms for @onChange. Set to 0 for immediate. @default 300 */
    debounce?: number;
  };
  Element: HTMLInputElement;
}

// ---------------------------------------------------------------------------
// Size classes
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Record<
  SearchInputSize,
  { input: string; iconSize: number; iconLeft: string; clearRight: string }
> = {
  sm: {
    input: 'h-[32px] py-1 pl-8 pr-8 text-sm',
    iconSize: 14,
    iconLeft: 'left-2',
    clearRight: 'right-1.5',
  },
  md: {
    input: 'h-[40px] py-2 pl-10 pr-10 text-sm',
    iconSize: 16,
    iconLeft: 'left-3',
    clearRight: 'right-2.5',
  },
  lg: {
    input: 'h-[48px] py-2.5 pl-11 pr-11 text-base',
    iconSize: 18,
    iconLeft: 'left-3.5',
    clearRight: 'right-3',
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined SearchInput — a search-specific input with magnifying glass icon
 * and clear button.
 *
 * Fires `@onChange` on every keystroke and `@onSearch` on Enter.
 * The clear button appears when there's a value and fires `@onClear`.
 *
 * @example Basic usage
 * ```gts
 * import SearchInput from 'tio-reimagined/components/search-input';
 *
 * <SearchInput
 *   @value={{this.query}}
 *   @onChange={{this.setQuery}}
 *   @onSearch={{this.performSearch}}
 *   @placeholder="Search employees…"
 * />
 * ```
 *
 * @example With label
 * ```gts
 * <SearchInput
 *   @label="Find a program"
 *   @value={{this.programQuery}}
 *   @onChange={{this.setProgramQuery}}
 *   @onClear={{this.clearProgramSearch}}
 * />
 * ```
 */
export default class SearchInput extends Component<SearchInputSignature> {
  private _fieldId = `reimagined-search-${guidFor(this)}`;

  private get size(): SearchInputSize {
    return this.args.size ?? 'md';
  }

  private get sizeClasses() {
    return SIZE_CLASSES[this.size];
  }

  private get placeholder(): string {
    return this.args.placeholder ?? 'Search…';
  }

  private get hasValue(): boolean {
    return Boolean(this.args.value?.length);
  }

  private get inputClasses(): string {
    const base = [
      'w-full rounded-lg border bg-white',
      'text-gray-900',
      'placeholder:text-gray-400',
      'transition-colors duration-150',
      'focus:outline-none',
      '[&::-webkit-search-cancel-button]:hidden',
      '[&::-webkit-search-decoration]:hidden',
      this.sizeClasses.input,
    ];

    if (this.args.isDisabled) {
      base.push('opacity-50 cursor-not-allowed border-gray-200');
    } else {
      base.push(
        'border-gray-300',
        'hover:border-gray-400',
        'focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20'
      );
    }

    return base.join(' ');
  }

  private get debounceMs() {
    return this.args.debounce ?? DEFAULT_DEBOUNCE_MS;
  }

  private _fireOnChange = (value: string) => {
    this.args.onChange?.(value);
  };

  handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const delay = this.debounceMs;

    if (delay > 0) {
      debounce(this, this._fireOnChange, input.value, delay);
    } else {
      this.args.onChange?.(input.value);
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = (event.target as HTMLInputElement).value;
      this.args.onSearch?.(value);
    }
  };

  handleClear = () => {
    this.args.onChange?.('');
    this.args.onClear?.();
  };

  <template>
    <FormField
      @label={{@label}}
      @hint={{@hint}}
      @isDisabled={{@isDisabled}}
      @size={{this.size}}
      @fieldId={{this._fieldId}}
      as |field|
    >
      <div class="relative">
        {{! Search icon }}
        <span
          class="absolute
            {{this.sizeClasses.iconLeft}}
            top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          aria-hidden="true"
        >
          <PhMagnifyingGlass @size={{this.sizeClasses.iconSize}} />
        </span>

        <input
          data-test-search-input
          id={{field.fieldId}}
          type="search"
          value={{@value}}
          placeholder={{this.placeholder}}
          disabled={{@isDisabled}}
          aria-describedby={{if @hint (concat field.fieldId "-hint")}}
          class={{this.inputClasses}}
          {{on "input" this.handleInput}}
          {{on "keydown" this.handleKeyDown}}
          ...attributes
        />

        {{! Clear button — hidden when disabled }}
        {{#if (and this.hasValue (not @isDisabled))}}
          <button
            data-test-search-clear
            type="button"
            class="absolute
              {{this.sizeClasses.clearRight}}
              top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Clear search"
            {{on "click" this.handleClear}}
          >
            <PhX @size={{this.sizeClasses.iconSize}} />
          </button>
        {{/if}}
      </div>
    </FormField>
  </template>
}

export type { SearchInputSignature };
